import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, Pagination, type Column } from '@/components/ui/DataTable';
import { Banner, ErrorState } from '@/components/ui/Feedback';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/app/PlaceholderPage';
import { useAuth } from '@/features/auth/AuthContext';
import { ApiError } from '@/lib/api';
import { databaseApi, type IndividualListItem } from './api';
import { FilterBar } from './FilterBar';
import { SelectionBar } from './SelectionBar';

/** L-05c — Daftar Individu PELITA. */
export function PelitaListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [tbmFilter, setTbmFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const canDelete = user?.role === 'super_admin';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['individuals', { q, status, tbmFilter, page }],
    queryFn: () =>
      databaseApi.listIndividuals({
        q: q || undefined,
        status: status || undefined,
        tbmId: tbmFilter || undefined,
        page,
      }),
  });

  const ids = [...selected];

  // Nama diambil dari baris yang sedang tampil supaya dialog bisa menyebut
  // siapa saja yang akan dihapus, bukan cuma "5 data".
  const selectedNames = (data?.data ?? []).filter((r) => selected.has(r.id)).map((r) => r.name);

  const deleteMutation = useMutation({
    mutationFn: () => databaseApi.bulkDeleteIndividuals(ids),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['individuals'] });
      queryClient.invalidateQueries({ queryKey: ['tbm'] });
      setSelected(new Set());
      setConfirming(false);
      setPage(1);
      setBanner(`${res.deleted} data PELITA SLI dihapus.`);
    },
    onError: (err) => {
      setConfirming(false);
      setBanner(err instanceof ApiError ? err.message : 'Gagal menghapus data.');
    },
  });

  const columns: Column<IndividualListItem>[] = [
    {
      key: 'code',
      header: 'Kode',
      render: (r) => <span className="tabular text-ink-secondary">{r.code}</span>,
      hideOnCard: true,
    },
    {
      key: 'name',
      header: 'Nama',
      render: (r) => (
        <div className="flex items-center gap-3">
          {r.photoKey ? (
            <img
              src={`/api/v1/files/${r.photoKey}`}
              alt={r.name}
              className="size-9 rounded-full object-cover border border-border shadow-xs shrink-0"
            />
          ) : (
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
              {r.name.slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <span className="font-medium text-ink block">{r.name}</span>
            {r.personalBranding && (
              <span className="text-xs text-ink-muted block truncate max-w-xs">
                {r.personalBranding}
              </span>
            )}
          </div>
        </div>
      ),
      hideOnCard: true,
    },
    {
      key: 'tbm',
      header: 'TBM & Wilayah',
      render: (r) =>
        r.tbmName ? (
          <div className="flex flex-col">
            <span className="font-medium text-ink">{r.tbmName}</span>
            {(r.regionName || r.cityName || r.provinceName) && (
              <span className="text-xs text-ink-muted">
                {[r.regionName, r.cityName, r.provinceName].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
        ) : (
          <span className="rounded-full bg-warn-soft px-2.5 py-0.5 text-[13px] text-[#9a6400]">
            Belum ada TBM
          </span>
        ),
    },
    {
      key: 'profession',
      header: 'Profesi / Pendidikan',
      render: (r) => {
        if (!r.profession && !r.lastEducation) return <span className="text-ink-muted">—</span>;
        return (
          <div className="text-sm">
            {r.profession && <span className="text-ink">{r.profession}</span>}
            {r.lastEducation && (
              <span className="ml-1 text-xs text-ink-secondary">({r.lastEducation})</span>
            )}
          </div>
        );
      },
      hideOnCard: true,
    },
    {
      key: 'gender',
      header: 'L/P',
      render: (r) => r.gender ?? '—',
      hideOnCard: true,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusBadge status={r.status} />,
      hideOnCard: true,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Database PELITA SLI"
        description="Profil individu Penggiat Literasi yang tergabung di TBM binaan."
        action={
          user?.role === 'super_admin' && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => navigate('/database/kelas-intensif')}>
                🎓 Kelas Intensif
              </Button>
              <Button variant="secondary" onClick={() => navigate('/database/pelita/impor')}>
                ⭳ Impor
              </Button>
              <Button onClick={() => navigate('/database/pelita/baru')}>+ PELITA SLI Baru</Button>
            </div>
          )
        }
      />

      {banner && (
        <Banner tone={banner.startsWith('Gagal') ? 'danger' : 'success'}>{banner}</Banner>
      )}

      <Card padded={false}>
        <SelectionBar
          count={selected.size}
          label="data PELITA SLI"
          onClear={() => setSelected(new Set())}
          onDelete={() => setConfirming(true)}
        />

        <FilterBar
          q={q}
          onQ={(v) => {
            setQ(v);
            setPage(1);
          }}
          placeholder="Cari nama PELITA SLI…"
          filters={[
            {
              label: 'Status',
              value: status,
              onChange: (v) => {
                setStatus(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Semua status' },
                { value: 'active', label: 'Aktif' },
                { value: 'inactive', label: 'Nonaktif' },
                { value: 'alumni', label: 'Alumni' },
              ],
            },
            {
              label: 'TBM',
              value: tbmFilter,
              onChange: (v) => {
                setTbmFilter(v);
                setPage(1);
              },
              // Tanpa filter ini, orang yang TBM-nya terhapus hanya bisa
              // ditemukan dengan menyisir seluruh daftar satu per satu.
              options: [
                { value: '', label: 'Semua TBM' },
                { value: 'none', label: 'Belum ada TBM' },
              ],
            },
          ]}
        />

        {error ? (
          <div className="p-4">
            <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
          </div>
        ) : (
          <>
            <div className="px-2 pb-2 lg:px-0">
              <DataTable
                columns={columns}
                rows={data?.data ?? []}
                rowKey={(r) => r.id}
                loading={isLoading}
                onRowClick={(r) => navigate(`/database/pelita/${r.id}`)}
                selection={canDelete ? { selected, onChange: setSelected } : undefined}
                cardTitle={(r) => r.name}
                cardSubtitle={(r) => {
                  const loc = [r.regionName, r.cityName].filter(Boolean).join(', ');
                  if (!r.tbmName) return 'Tanpa TBM aktif';
                  return loc ? `${r.tbmName} (${loc})` : r.tbmName;
                }}
                cardBadge={(r) => <StatusBadge status={r.status} />}
                empty={{
                  title: q || status ? 'Tidak ada PELITA SLI yang cocok' : 'Belum ada data PELITA SLI',
                  description:
                    q || status
                      ? 'Coba ubah kata kunci atau filter.'
                      : 'Impor dari spreadsheet, atau tambahkan satu per satu.',
                }}
              />
            </div>
            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <ConfirmDialog
        open={confirming}
        title={`Hapus ${ids.length} data PELITA SLI?`}
        confirmLabel={deleteMutation.isPending ? 'Menghapus…' : 'Ya, hapus permanen'}
        loading={deleteMutation.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => deleteMutation.mutate()}
      >
        {selectedNames.length > 0 && (
          <ul className="mb-3 max-h-40 overflow-y-auto rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-[14px]">
            {selectedNames.map((name) => (
              <li key={name} className="truncate py-0.5">
                {name}
              </li>
            ))}
          </ul>
        )}

        <p>
          Tindakan ini <strong>tidak bisa dibatalkan</strong>. Kalau orangnya hanya berhenti aktif,
          ubah statusnya menjadi <em>Alumni</em> supaya datanya tetap tersimpan.
        </p>
      </ConfirmDialog>
    </div>
  );
}
